import React, { useState } from 'react';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { ShareIcon, Copy, Mail } from 'lucide-react';

import {
    resolvePropertyContacts,
    useContacts,
    useProperties,
    type ILead,
    type IProperty,
} from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useNotifications } from 'src/notifications';
import { LeadUtils } from 'src/utils';
import { copyToClipboard } from 'src/components/copy-text-button/utils';
import { formatLeadShareText, getShareLeadMailtoLink } from 'src/components/lead-sidebar/share-utils';
import { useTouringHours } from 'src/services/api/touring-hours/use-touring-hours';
import { getEffectiveTourTimezone } from 'src/utils/format-date-time';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';

import styles from 'src/components/lead-sidebar/ShareLeadButton.module.css';

type Props = {
    lead: ILead;
    tours: IShowing[];
    /**
     * If the lead is associated with a specific property
     */
    property?: IProperty | Pick<IProperty, 'id' | 'name'>;
};

export default function ShareLeadButton({ lead, tours, property }: Props) {
    const { userSettings } = useAuthenticatedState();
    const { addNotification } = useNotifications();
    const { data: contacts } = useContacts();
    const { data: properties } = useProperties();

    // Onsite team recipients come from the property's contacts (own or inherited
    // from the nearest ancestor), resolved locally from the cached lists.
    const onsiteTeamRecipients = property?.id != null && contacts && properties ?
        resolvePropertyContacts(contacts, properties, property.id).map((contact) => ({
            email: contact.email,
            name: contact.name ?? undefined,
        })) :
        [];

    const [anchorEl, setAnchorEl] = useState<HTMLElement>();
    const upcomingTour = tours
        .filter((tour) =>
            tour.lead_uuid === lead.uuid &&
            !tour.is_canceled &&
            !dayjs(tour.start_time).isBefore(dayjs())
        )
        .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())[0];

    const { data: touringHours, isLoading: isTouringHoursLoading } = useTouringHours(
        upcomingTour?.property_id ?? null,
    );
    // Anchor tour times to the tour's own timezone, falling back to the building's
    // timezone, then the user's — matching every other tour surface.
    const { timezone } = getEffectiveTourTimezone(
        upcomingTour?.timezone ?? touringHours?.timezone,
        userSettings,
    );

    const isResolvingTimezone = !upcomingTour?.timezone && isTouringHoursLoading;

    const shareParams = {
        lead,
        leadName: LeadUtils.getLeadName(lead),
        propertyName: property?.name,
        upcomingTour,
        timezone,
    };

    // Only enabled when the property has resolved contacts (see `disabled` below).
    const mailtoHref = onsiteTeamRecipients.length > 0 ?
        getShareLeadMailtoLink({ ...shareParams, recipients: onsiteTeamRecipients }) :
        undefined;

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        analyticsClient.track(AnalyticsEvent.CLICKED_SHARE_LEAD, { lead_uuid: lead.uuid });
        setAnchorEl(event.currentTarget);
    };

    const handleCopyToClipboard = async () => {
        const text = formatLeadShareText(shareParams);

        const isSuccess = await copyToClipboard(text);
        analyticsClient.track(AnalyticsEvent.COPIED_LEAD_TO_CLIPBOARD, {
            lead_uuid: lead.uuid,
            success: isSuccess,
        });

        if (isSuccess) {
            addNotification('Lead details copied to clipboard', 'success');
        } else {
            addNotification('Failed to copy lead details', 'error');
        }

        setAnchorEl(undefined);
    };

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                fullWidth
                className={styles.shareBtn}
                onClick={handleOpenMenu}
                startIcon={<ShareIcon size={16} />}
                data-testid="share-lead-btn"
            >
                Share
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(undefined)}
            >
                <MenuItem
                    onClick={handleCopyToClipboard}
                    className={styles.menuItem}
                    data-testid="share-lead-copy-to-clipboard"
                    disabled={isResolvingTimezone}
                >
                    <Copy size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>
                        Copy to Clipboard
                    </Typography>
                </MenuItem>
                <MenuItem
                    component={Link}
                    href={mailtoHref}
                    className={styles.menuItem}
                    data-testid="email-lead-link"
                    disabled={!mailtoHref || isResolvingTimezone}
                >
                    <Mail size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>
                        Draft Email to Onsite Team
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
}
